import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SaasTenantDto } from '@volo/abp.ng.saas/proxy';
import { TenantCloneStateService } from '../tenant-clone-state.service';
import { ToasterService } from '@abp/ng.theme.shared';
import { finalize } from 'rxjs/operators';
import { CustomTenantService } from '@proxy/custom-tenant-service';

@Component({
    selector: 'app-tenant-clone-popup',
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './tenant-clone-popup.component.html',
    standalone: false
})
export class TenantClonePopupComponent implements OnInit {
  @Input() open = false;
  @Output() modalClose = new EventEmitter<boolean>();

  form: FormGroup;
  readonly isBusy = signal(false);
  readonly visiblePasswords = signal({
    adminPassword: false,
    confirmAdminPassword: false,
    AssetPassword: false,
  });
  originalTenant: SaasTenantDto;

  constructor(
    private cloneState: TenantCloneStateService,
    private toasterService: ToasterService,
    private fb: FormBuilder,
    private customTenantService: CustomTenantService
  ) {}

  togglePassword(field: 'adminPassword' | 'confirmAdminPassword' | 'AssetPassword'): void {
    this.visiblePasswords.update(v => ({ ...v, [field]: !v[field] }));
  }

  ngOnInit(): void {
    const data = this.cloneState.getCloneData();
    if (data) {
      this.originalTenant = data;
      this.buildForm(data);
      this.cloneState.clearCloneData();
    } else {
      this.buildForm();
    }
  }

  buildForm(sourceData?: SaasTenantDto) {
    // Destructure and set defaults similar to create-request.component.ts
    const { name, activationState, extraProperties = {} } = sourceData || ({} as SaasTenantDto);
    this.form = this.fb.group(
      {
        name: [`${name || ''}-copy`, [Validators.required, Validators.maxLength(64)]],
        adminEmailAddress: [
          extraProperties['AdminEmailAddress'] ?? '',
          [Validators.required, Validators.email, Validators.maxLength(128)],
        ],
        adminPassword: [
          '',
          [Validators.required, Validators.minLength(6), Validators.maxLength(128)],
        ],
        confirmAdminPassword: ['', Validators.required],
        activationState: [activationState === 2 ? 2 : 0, Validators.required],
        extraProperties: this.fb.group({
          AdminUserName: [
            extraProperties['AdminUserName'] ?? 'admin',
            [Validators.required, Validators.maxLength(256)],
          ],
          TradeName: [
            extraProperties['TradeName'] ?? '',
            [Validators.required, Validators.maxLength(128)],
          ],
          Email: [
            extraProperties['Email'] ?? '',
            [Validators.required, Validators.email, Validators.maxLength(128)],
          ],
          AssetPassword: [extraProperties['AssetPassword'] ?? '', Validators.required],
          IsRequired: [
            extraProperties['IsRequired'] === true || extraProperties['IsRequired'] === 'True',
            [],
          ],
        }),
      },
      { validators: this.passwordMatchValidator }
    );
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('adminPassword')?.value;
    const confirmPasswordControl = group.get('confirmAdminPassword');

    if (password !== confirmPasswordControl?.value) {
      confirmPasswordControl?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      // remove the error if passwords match
      if (confirmPasswordControl?.hasError('passwordMismatch')) {
        confirmPasswordControl.setErrors(null);
      }
      return null;
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;
    const cloneInput = {
      sourceTenantId: this.originalTenant?.id,
      tenantOptions: {
        name: formValue.name,
        adminEmailAddress: formValue.adminEmailAddress,
        adminPassword: formValue.adminPassword,
        activationState: formValue.activationState,
        extraProperties: {
          ...formValue.extraProperties,
          ConfirmAdminPassword: formValue.confirmAdminPassword,
        },
      },
    };

    this.isBusy.set(true);
    this.toasterService.info('SaasService::TenantCloningInProgress');
    this.customTenantService
      .clone(cloneInput)
      .pipe(finalize(() => this.isBusy.set(false)))
      .subscribe(() => {
        this.toasterService.success('SaasService::TenantClonedSuccessfully');
        this.modalClose.emit(true);
      });
  }

  close(): void {
    this.modalClose.emit(false);
  }
}
